import React, { useEffect, useRef, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"

/**
 * Sliding carousel: auto-advance (`autoplay`, every `interval` ms), prev/next buttons, dot indicators, swipe.
 * Children are the slides (each fills the carousel height).
 */
export default function Carousel({ children, autoplay = true, interval = 5000, duration = 1000, height = "450px", animation = "slide", onIndexChange }) {
  const slides = React.Children.toArray(children)
  const count = slides.length
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const touch = useRef(null)

  // the slide shown, for whoever counts what was seen (ad impressions)
  const changed = useRef(onIndexChange)
  changed.current = onIndexChange
  useEffect(() => {
    if (count) changed.current?.(index)
  }, [index, count])

  useEffect(() => {
    if (!autoplay || count <= 1 || paused) return
    const id = setInterval(() => setIndex((i) => (i + 1) % count), interval)
    return () => clearInterval(id)
  }, [autoplay, count, interval, paused])

  if (count === 0) return null
  const go = (i) => setIndex(((i % count) + count) % count)

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touch.current === null) return
        const dx = e.changedTouches[0].clientX - touch.current
        touch.current = null
        if (Math.abs(dx) < 40) return
        go(dx < 0 ? index + 1 : index - 1)
      }}
    >
      {animation === "fade" ? (
        <div className="relative h-full">
          {slides.map((slide, i) => (
            <div key={i} className="absolute inset-0 h-full" style={{ opacity: i === index ? 1 : 0, transition: `opacity ${duration}ms ease-in-out`, pointerEvents: i === index ? "auto" : "none" }} aria-hidden={i !== index}>
              {slide}
            </div>
          ))}
        </div>
      ) : (
      <div
        className="flex h-full"
        style={{
          width: `${count * 100}%`,
          transform: `translateX(-${(index * 100) / count}%)`,
          transition: `transform ${duration}ms ease-in-out`,
        }}
      >
        {slides.map((slide, i) => (
          <div key={i} className="h-full" style={{ width: `${100 / count}%` }} aria-hidden={i !== index}>
            {slide}
          </div>
        ))}
      </div>
      )}

      {count > 1 && (
        <>
          {/* the arrows are a wide screen's; a phone swipes and has the dots */}
          <button
            type="button"
            aria-label="Previous"
            onClick={() => go(index - 1)}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white/30 text-text hover:bg-white/70 transition-colors"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={() => go(index + 1)}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white/30 text-text hover:bg-white/70 transition-colors"
          >
            <ChevronRightIcon className="w-6 h-6" />
          </button>
          <div className="absolute bottom-2 left-0 right-0 z-10 flex justify-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Slide ${i + 1}`}
                onClick={() => go(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${i === index ? "bg-primary" : "bg-white/60 hover:bg-white"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
